/**
 * A component that manages an instance of the BoB pixel.
 *
 * > The "bunch of browsers" (BoB) pixel is the Nexstar Data Sciences team's
 * > proprietary data collection tracking pixel used to track video and video ad
 * > views separately from Google Analytics. It ties into their custom systems,
 * > and is utilized to produce reports on inventory.
 *
 * @see https://alleyinteractive.atlassian.net/browse/LAK-197.
 */
export default class BobPixel {
  /**
   * URL object for the pixel.
   *
   * @type {URL}
   */
  src;

  /**
   * Constructor.
   *
   * @param {Element} parentEl Pixel parent element.
   * @param {String} id Pixel ID.
   * @param {Object} variables Create the pixel immediately with these variables.
   * @param {String} bobBase Base hostname for BOB tag.
   */
  constructor(parentEl, id, variables, bobBase) {
    this.parentEl = parentEl;
    this.id = id;

    // @todo Send from the server.
    this.src = new URL(`https://${bobBase}/bob_003.gif`);

    if (variables) {
      this.appendPixelToParent();
      this.updatePixel(variables);
    }
  }

  /**
   * Create the pixel element in a wrapper and add the wrapper to the parent.
   */
  appendPixelToParent() {
    if (this.pixel) {
      return;
    }

    const wrapper = document.createElement('div');
    wrapper.classList.add('screen-reader-text');
    this.parentEl.appendChild(wrapper);

    this.pixel = document.createElement('img');
    this.pixel.id = this.id;
    this.pixel.alt = '';
    this.pixel.style.width = '1px';
    wrapper.appendChild(this.pixel);
  }

  /**
   * Update the pixel src with new variables, thereby sending another request.
   *
   * @param {Object} variables New pixel variables, if any.
   */
  updatePixel(variables) {
    if (! this.pixel) {
      this.appendPixelToParent();
    }

    // Delete all the existing parameters.
    Array.from(this.src.searchParams.keys()).forEach((key) => {
      this.src.searchParams.delete(key);
    });

    Object
      .keys(variables)
      .forEach((key) => {
        this.src.searchParams.append(key, variables[key]);
      });

    this.pixel.src = this.src.toString();
  }
}
